import React from "react";
import { Lead, Section, P, Bullets, Callout, Img, CTA } from "../../../../blog/components";
import shotThreePrompts from "../../../../assets/blog/dashboard-three-prompts.jpg";
import shotDefault from "../../../../assets/blog/dashboard-default-llm.jpg";

export const title = "为什么大模型设计不出仪表盘";
export const excerpt =
  "向任何一个模型要一个仪表盘，你都会拿到同一个：四张卡片、一条折线图、一个环形图、一张表格。代码写得很漂亮，设计却是空的。这是一篇关于原因的观点文章。";
export const heroAlt = "墨比斯风格的平原，一路平铺到地平线";
export const readingTime = "阅读约 8 分钟";

export default function Post() {
  return (
    <>
      <Lead>
        模型能写出非常漂亮的仪表盘代码，但它并不会设计仪表盘。这是两种不同的能力，而训练数据里只有其中一种。
      </Lead>

      <P>
        你可以自己试。要一个数据分析仪表盘、一个 SaaS 后台面板、一个给支付团队用的视图。产品不同、用户不同、要做的决策也不同，可你会拿回同一个页面三次，只是主色换了一轮。
      </P>

      <Img
        src={shotThreePrompts}
        alt="来自三个不同提示词的三个仪表盘，结构完全一致"
        caption="三个提示词，一种布局。为本文重建的默认输出效果。"
      />

      <P>
        左侧六到七项的侧边栏。顶部四张指标卡。一张带渐变填充的宽面积图。旁边一个环形图。底部一张最近动态表。所有卡片一样大，所有数字一样重，所有涨跌都是绿色向上。
      </P>

      <Section title="默认的那个仪表盘">
        <P>
          下面是它的完整版本：一次成型的提示词，没有任何额外指示。它没有任何地方是坏的。间距统一、字号层级合理、对比度达标、组件可复用。作为一份前端工程，它比大多数手写出来的东西都要好。
        </P>

        <Img
          src={shotDefault}
          alt="一个典型的 AI 生成仪表盘：紫色渐变、四张一模一样的指标卡、面积图、环形图和最近销售表"
          caption="你默认会拿到的形状。它刚好放得下一屏，也刚好什么都没说。"
        />

        <P>
          现在试着用它。今天早上生意还好吗？有人需要做点什么吗？这四个数字里，哪一个值得半夜把人叫醒？这个界面没有观点。它把收入、订阅、销量和在线用户放在完全相同的视觉权重上，因为它不知道哪个更重要，也没有任何办法知道。
        </P>
        <P>
          如果这些数字看着眼熟，那是应该的。“$45,231.89，环比增长 20.1%”和“Olivia Martin，olivia.martin@email.com”来自一个被大量复制的组件库示例。模型不是在发明一个仪表盘，它是在背诵一个。
        </P>
      </Section>

      <Section title="问题不在代码">
        <P>
          很容易把这件事归成“AI 的 CSS 写不好”。并不是。失败发生在代码之上的一层，在任何东西被渲染之前的那部分设计里：
        </P>
        <Bullets
          items={[
            "决定这块屏幕是为什么存在的，以及不是为什么存在的。",
            "决定哪一个数字有资格成为页面上最大的东西。",
            "决定砍掉什么，好让重要的东西有地方放。",
            "决定当那个数字变糟时，看的人下一步该做什么。",
          ]}
        />
        <P>
          这些都不是样式选择，而是关于业务的判断。一个仪表盘无非就是这些判断的总和，用字号表达出来。每一个仪表盘都是一场关于“什么重要”的论证。一个在结果里没有任何利害关系的模型，没有立场要辩护，于是它交出唯一一种不需要表态的布局：一切，同等权重。
        </P>
        <Callout>
          同等权重是布局的零假设。它是你还什么都没决定时会得到的东西。
        </Callout>
      </Section>

      <Section title="为什么会是这样">
        <P>
          我有四个猜想。没有一个是“模型还不够聪明”，那在我看来是所有解释里最没意思的一个。
        </P>
      </Section>

      <Section title="一、它学到的是照片，不是这份工作">
        <P>
          真正在用的运维仪表盘都在登录之后。故障期间支持负责人盯着的那块屏，仓库主管挂在墙上的那块屏，交易台真正在读的那块屏。这些都不在公开网络上，因此也都不在训练数据里。
        </P>
        <P>
          公开的是什么：作品集截图、模板市场、组件库示例、教程仓库、灵感图库。这些东西无一例外都是为了在缩略图里好看，给一个永远不会真正使用它的人看的。这跟“凌晨三点被一个已经熟悉系统的人一眼读懂”是完全不同的目标函数。
        </P>
        <P>
          所以模型近乎完美地学会了这一类图像的体裁惯例，只不过是从营销素材里学的。你要一个仪表盘，它给你一张仪表盘的照片，而这正是它被展示过的东西。
        </P>
      </Section>

      <Section title="二、设计是做减法，而减法没有回报">
        <P>
          好的运维界面基本上都是删出来的。你删掉那个从来没人据此行动过的指标、那个只因为有数据才存在的图表、第三个筛选器、创始人 2023 年要求加上的那个虚荣数字。活下来的东西又小又密、还有点丑，但它管用。
        </P>
        <P>
          现在从模型的位置看“删除”是什么。加一张卡片是免费的，而且看上去很周到。删一张卡片则是在赌用户本来就不想要它，一旦赌错，回答就显得偷懒或者不完整。奖励“有帮助”的训练，最终奖励的是覆盖面。而覆盖面意味着更多卡片。
        </P>
        <P>
          于是我们要求一个结构上偏加法的系统，去干一门结构上是减法的手艺。它选择两头兼顾，而在布局里，两头兼顾看起来就是一整片大小相同的卡片网格。
        </P>
      </Section>

      <Section title="三、它没有利害关系，所以排不出优先级">
        <P>
          层级来自后果。你把某个数字放得很大，是因为在这个数字上反应慢了会赔钱，或者会把人从床上叫起来。这种知识不在提示词里，通常也不在代码库里。它在运营者的脑子里，在上个季度出过什么事里，在周一哪位高管会问哪个问题里。
        </P>
        <P>
          让模型设计仪表盘，等于让它给一堆自己观察不到后果的东西排序。在那种不确定性下，拒绝排序其实是正确的行为。问题在于，我们把这份拒绝读成了设计，因为它交付时长得就像一份设计。
        </P>
      </Section>

      <Section title="四、“像”本身就是错的目标">
        <P>
          显而易见的反驳：现在的模型可以看图了。把输出图丢回给一个多模态模型，它会很乐意给你评审一番。
        </P>
        <P>
          它确实会，但评审的是“像不像”。这看起来像专业仪表盘吗？像。间距一致吗？一致。可以发布。它做不到的，是设计师看着自己那块屏时会产生的那种轻微的生理性不适：因为他知道用这块屏的人只有九十秒，而答案不在页面上。那种不适来自亲眼看过别人使用这个东西。它不是一种视觉属性，所以看多久都找不回来。
        </P>
      </Section>

      <Section title="我欠你一张截图，但我给不出来">
        <P>
          按惯例，文章写到这里该放“之后”的那张图了：同样的数据，做对了的版本，好让你看出区别。我做了一张。它没有出现在这里，而它没出现的原因，正是这篇文章的论点。
        </P>
        <P>
          那张图是由渲染出第一张截图的同一台机器渲染的，只不过这次依据的是我的排序，而不是它自己的：一个巨大的失败计数、在引发问题的那次发布上标出的尖峰、按损失金额排序的表格、以及原本放环形图的位置换成的一条回滚建议。它看上去比默认版本严肃多了。可一旦把它摆在真正会把人从床上叫起来的那块屏幕旁边，它依然只是一张仪表盘的照片；把它登在这里，恰恰就是这篇文章在抱怨的那个动作：一个为了让永远不会使用它的人看一眼而优化过的界面。
        </P>
        <P>
          因为让一块运维屏幕变好的东西，在截图里根本看不见。它是使用留下的沉淀物：是某次故障后的第二周有人坚持要加回来的那一列，是发现从来没人据此行动过而被砍掉的那个指标，是改了三次才终于不再在凌晨四点把人吵醒的那个阈值，是只有当你知道哪家供应商的状态页会撒谎时才讲得通的那个行序。这些都不是视觉属性。它们渲染不出来，我也没法伪造得足够像，值得占用你的时间。
        </P>
        <P>
          这同时也解释了：拍照最好看的仪表盘，往往是还没有人用过的那些。前面说的训练数据问题，这下找上了我自己家门口。所以，请拿描述代替图片：这块屏幕是给值班的人用的，它一眼说明结账是不是坏了，并且说明该做什么。它好不好，不由它在这里好不好看来决定，而是由凌晨四点、由一个并非自愿醒着的人来决定。
        </P>
      </Section>

      <Section title="剩下的是一个渲染器，不是设计师">
        <P>
          不过要注意那次尝试里真正发生了什么。屏幕是模型渲染出来的，但不是模型设计的。上面没有任何一个决定是它提出来的：不是哪个数字有资格变得巨大，不是那个尖峰需要把发布标在上面，不是环形图必须去掉，也不是一条回滚建议比再加四个指标更值钱。这些全都是被交给它的，而且是分好几轮交过去的。任何一步只要放手让它自己判断，它就会退回原样：再加一张卡片，因为有数据就再加一张图，在一个没人会据此行动的数字上挂一个绿色的涨幅。
        </P>
        <P>
          所以这不是在抱怨技术，而是在说价值转移到了哪里。渲染已经被解决了，这本身确实很有价值。没有转移的，是关于“什么重要”的判断，而且模型再强也不会把它挪过来，因为这份输入根本不在机器里面。
        </P>
        <P>
          这份判断的实际形态又小又乏味，所以才特别容易被跳过：
        </P>
        <Bullets
          items={[
            "说清楚谁在看这块屏，在什么时刻看。",
            "说清楚哪一个数字决定他要不要行动。",
            "说清楚他接下来要做什么，并把这件事也放上屏幕。",
            "明确说出该砍掉什么，因为它自己什么都不会砍。",
          ]}
        />
        <P>
          把这些给模型，它会很快用你喜欢的任何框架把东西搭出来，实打实替你省下一天。什么都不给，它就会做出全互联网仪表盘截图的平均值：一个它确实学会了的东西，也是一个没什么好看的东西。而它永远不会做的，是提醒你：你还没有做决定。
        </P>
        <P>
          让人不舒服的是，这个结论对大部分智能体工作都成立，不只是仪表盘。瓶颈从来就不是打字。
        </P>
      </Section>

      <Callout>
        每一个仪表盘都是有人在说“这才是重要的”。如果没有人说过这句话，那块屏幕就什么也不会说，无论它被做得多好。
      </Callout>

      <CTA label="认识 CATE" href="/" />
    </>
  );
}
